<template>
  <div>
    <MobialisFormWithValidation
      @onValidate="submit"
      novalidate
      v-slot="{ errors }"
      v-av-analytics-form-submit="{ elementText: getSubmitButtonTitle }"
    >
      <h2 class="mb-3 mb-sm-4">{{ heading }}</h2>
      <div v-if="!hasTradeVehicleOnEnter" class="row form-row pb-2 pt-2">
        <div class="col-sm-10">
          <MobialisFancyRadio
            class="btn-options"
            rules="required"
            vid="hasTradeIn"
            v-model="hasTradeIn"
            :textArray="tradeInOptions"
            name="tradeInOptions"
            data-cy="hasTradeIn"
          ></MobialisFancyRadio>
        </div>
        <p class="text-danger ml-2 small my-0" v-if="errors.hasTradeIn && errors.hasTradeIn.length > 0">
          {{ $t('message.credit.application.tradeInVehicle.pleaseSelect') }}
        </p>
      </div>
      <div class="row trade-details-form">
        <div v-if="hasTradeInVehicle" class="col-sm-10">
          <h3 v-if="!hasTradeVehicleOnEnter" class="pt-sm-4 pt-3 pb-sm-3 pb-2">
            {{ $t('message.credit.application.tradeInVehicle.vehicleHeading') }}
          </h3>
          <trade-auto-complete
            :disabled="hasTradeVehicleOnEnter"
            v-model="vehicleSelected"
            :valueObject="selectedVehicleObject"
            :label="$t('message.credit.application.tradeInVehicle.yearMakeModel')"
            :placeholder="$t('message.trade.autoFillPlaceHolder')"
            @selectedVehicleObject="selectedTradeVehicle"
          >
          </trade-auto-complete>
          <mobialis-text-input-with-validation
            class="col-sm-6 p-0 mt-4 mb-0"
            :rules="{ required: true }"
            :label="$t('message.credit.application.tradeInVehicle.mileage')"
            :helper="mileageHelper"
            v-model="mileage"
            :mask-type="languageCode === 'fr-CA' ? 'frenchInt' : 'int'"
            inputmode="numeric"
            pattern="[0-9]*"
            vid="mileage"
            data-cy="mileage"
            :readonly="hasTradeVehicleOnEnter"
          ></mobialis-text-input-with-validation>
          <mobialis-text-input-with-validation
            v-if="!hasEquifaxTradeLines"
            class="col-sm-6 p-0 mt-4 mb-0"
            :rules="{ required: true }"
            :label="$t('message.credit.application.tradeInVehicle.currentBalance')"
            :helper="$t('message.credit.application.tradeInVehicle.currentBalanceHelper')"
            v-model.number="balanceOwing"
            :mask-type="languageCode === 'fr-CA' ? 'frenchInt' : 'moneyInt'"
            inputmode="numeric"
            pattern="[0-9]*"
            vid="balanceOwing"
            data-cy="balanceOwing"
          ></mobialis-text-input-with-validation>
          <div v-if="hasEquifaxTradeLines" class="mt-5">
            <h3 class="mb-3 mb-sm-4">
              {{ $t('message.credit.application.tradeInVehicle.askBalanceOnVehicle') }}
            </h3>
            <div class="row form-row pb-2 pt-2">
              <div class="col-sm-12">
                <MobialisFancyRadio
                  class="btn-options"
                  vid="hasBalanceOnVehicle"
                  rules="required"
                  v-model="hasBalanceOnVehicle"
                  :textArray="tradeInOptions"
                  name="balanceOnVehicleOptions"
                  data-test="balanceOnVehicleRadio"
                ></MobialisFancyRadio>
              </div>
            </div>
            <p
              class="text-danger ml-2 small my-0"
              v-if="errors.hasBalanceOnVehicle && errors.hasBalanceOnVehicle.length > 0"
            >
              {{ $t('message.credit.application.tradeInVehicle.pleaseSelect') }}
            </p>
          </div>
        </div>
      </div>
      <div>
        <mobialis-button type="submit" class="mt-5 mb-3" data-cy="continue"
          >{{ getSubmitButtonTitle }}
        </mobialis-button>
      </div>
    </MobialisFormWithValidation>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { enumMixin, avErrorMixin } from '@/mixins';
import TradeAutoComplete from '@/components/shared/TradeAutoComplete.vue';
import log from '@/services/log';

export default {
  name: 'TradeInVehicle',
  mixins: [enumMixin, avErrorMixin],
  components: {
    TradeAutoComplete,
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      getVehicleName: 'trade/getVehicleName',
      getBaseVehicleObject: 'trade/getBaseVehicleObject',
      hasEquifaxTradeLines: 'credit/hasEquifaxTradeLines',
      getThisIsAmerica: 'business/getThisIsAmerica',
      getProductHomeState: 'getProductHomeState',
    }),
    ...mapState('trade', ['vehicle']),
    mileageHelper() {
      return this.getThisIsAmerica ? '' : this.$t('message.credit.application.tradeInVehicle.mileageKm');
    },
    hasTradeInVehicle() {
      return this.hasTradeIn === this.enums.YesNo.YES;
    },
    previousVehicleIsSelected() {
      return this.vehicle && this.vehicle.year !== '';
    },
    heading() {
      return this.hasTradeVehicleOnEnter
        ? this.$t('message.credit.application.tradeInVehicle.headingV2')
        : this.$t('message.credit.application.tradeInVehicle.heading');
    },
    getSubmitButtonTitle() {
      return this.hasEquifaxTradeLines
        ? this.$t('message.common.continue')
        : this.$t('message.credit.viewAutoAffordability');
    },
  },
  data() {
    return {
      tradeInOptions: this.$t('message.credit.application.tradeInVehicle.options'),
      hasTradeIn: this.$store.state.trade.hasTradeIn,
      vehicleSelected: '',
      selectedVehicleObject: {},
      mileage: this.$store.state.trade.vehicle.mileage,
      balanceOwing: this.$store.state.trade.vehicle.balanceOwing,
      hasTradeVehicleOnEnter: false,
      balanceOnVehicleOptions: this.$t('message.credit.application.tradeInVehicle.options'),
      hasBalanceOnVehicle: this.$store.state.trade.hasBalanceOnVehicle,
    };
  },
  methods: {
    async submit(isValid) {
      if (!isValid) return;
      if (this.hasTradeInVehicle) {
        this.setTradeInVehicleData();
        try {
          await this.getTradeReportAndUpdateLead();
        } catch (error) {
          // @TODO: figure out what should happen if we don't get a trade report
          this.handleTradeReportError(error);
        }
        this.navigateToNextPage();
      } else {
        this.resetTradeInVehicle();
        this.navigateToNextPage();
      }
    },
    setTradeInVehicleData() {
      this.$store.commit('SET_LOADING', true);
      if (!this.hasTradeVehicleOnEnter) {
        this.$store.commit('trade/SET_VEHICLE', this.selectedVehicleObject);
      }
      this.$store.commit('trade/SET_VEHICLE_MILEAGE', this.mileage);
      if (!this.hasEquifaxTradeLines) {
        this.$store.commit('trade/SET_VEHICLE_BALANCE_OWING', this.balanceOwing);
      } else {
        this.$store.commit('trade/SET_HAS_BALANCE_ON_VEHICLE', this.hasBalanceOnVehicle);
      }
      this.$store.commit('trade/SET_TRADE_EQUITY_USED_FOR_CIM', true);
      this.$store.commit('trade/SET_HAS_TRADE_IN', true);
    },
    async getTradeReportAndUpdateLead() {
      if (this.hasBalanceOnVehicle === this.enums.YesNo.YES) {
        await this.$store.dispatch('credit/getTradeLines');
      }
      if (this.getProductHomeState.trade.state !== this.enums.DHSState.COMPLETE) {
        try {
          await this.$store.dispatch('trade/getReport');
        } catch (error) {
          log('Error on getting trade-in report', error);
        }
      }
      this.$store.commit('trade/SET_LEAD_TRADEIN_VEHICLE');
      await this.$store.dispatch('lead/updateLead');
    },
    handleTradeReportError(error) {
      this.$log('Error getting credit flow trade report', error);
    },
    navigateToNextPage() {
      let nextPage = `credit-results-${this.baseRoute}`;
      if (this.hasBalanceOnVehicle === this.enums.YesNo.YES) {
        nextPage = `select-auto-loan-${this.baseRoute}`;
      }
      this.$router.push({ name: nextPage });
      this.$store.commit('SET_LOADING', false);
    },
    resetTradeInVehicle() {
      this.$store.commit('trade/RESET_VEHICLE');
    },
    selectedTradeVehicle(vehicle) {
      this.selectedVehicleObject = vehicle;
    },
  },
  mounted() {
    this.vehicleSelected = this.getVehicleName;
    this.selectedVehicleObject = this.getBaseVehicleObject;
    if (this.previousVehicleIsSelected) {
      this.hasTradeVehicleOnEnter = true;
      this.hasTradeIn = this.enums.YesNo.YES;
    }
  },
};
</script>

<style lang="scss" scoped>
.trade-details-form {
  min-height: 300px;
  @media (max-width: $breakpoint-mobile) {
    min-height: 200px;
  }
}

h2 {
  font-size: 28px;
}

h5 {
  font-weight: 500;
}

p {
  font-size: 14px;
}

.btn-options::v-deep .radio-btn {
  float: left;
  width: 50%;
  padding-right: 10px;
}

@media (max-width: 479px) {
  .btn-options::v-deep .radio-btn {
    float: none;
    width: 100%;
  }
}
</style>
